'use client';

import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@/primitives';
import { slate } from '@/lib/colours';

export type TraitSettingsColumn = { field: string; label: string };

type TraitSettingsModalProps = {
  open: boolean;
  onClose: () => void;
  categoryId: string;
  categoryName: string;
  columns: TraitSettingsColumn[];
  showCategory: boolean;
  columnVisibility: Record<string, boolean>;
  onApply: (
    showCategory: boolean,
    columnVisibility: Record<string, boolean>,
  ) => void;
};

export default function TraitSettingsModal({
  open,
  onClose,
  categoryId: _categoryId,
  categoryName: _categoryName,
  columns,
  showCategory,
  columnVisibility,
  onApply,
}: TraitSettingsModalProps) {
  const [localShowCategory, setLocalShowCategory] = useState(showCategory);
  const [localColumnVisibility, setLocalColumnVisibility] =
    useState<Record<string, boolean>>(columnVisibility);

  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- resets the local draft state to the incoming props each time the modal opens; these states are also mutated by the in-modal toggle handlers, so they are editable working copies, not derived values
      setLocalShowCategory(showCategory);

      setLocalColumnVisibility(columnVisibility);
    }
  }, [open, showCategory, columnVisibility]);

  const handleClose = () => {
    onClose();
  };

  const handleApply = () => {
    onApply(localShowCategory, localColumnVisibility);
    onClose();
  };

  const handleToggleColumn = (field: string, checked: boolean) => {
    setLocalColumnVisibility((prev) => ({ ...prev, [field]: checked }));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Trait settings</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography
            variant="bodysmall"
            sx={{
              color: slate[600],
            }}
          >
            Show/hide this category or customise traits based on your
            preference.
          </Typography>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={localShowCategory}
                  onChange={(e) => setLocalShowCategory(e.target.checked)}
                />
              }
              label="Show this category"
            />
          </Box>

          <Box>
            <Typography
              variant="labellarge"
              sx={{ fontWeight: 600, mb: 1, display: 'block' }}
            >
              Column options:
            </Typography>
            <Stack
              spacing={0.5}
              sx={{
                maxHeight: 280,
                overflowY: 'auto',
                pl: 0.5,
              }}
            >
              {columns.map(({ field, label }) => (
                <FormControlLabel
                  key={field}
                  control={
                    <Checkbox
                      checked={localColumnVisibility[field] !== false}
                      onChange={(e) =>
                        handleToggleColumn(field, e.target.checked)
                      }
                      disabled={!localShowCategory}
                    />
                  }
                  label={label}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          Close
        </Button>
        <Button onClick={handleApply} variant="contained">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
